import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/ui/Button';
import Input from '../../../../../components/ui/Input';
import Switch from '../../../../../components/ui/Switch';
import StickyActionBar from '../../../../../components/ui/StickyActionBar';
import { Spinner } from '../../../../../components/ui/Spinner';
import { getWpsSettings, putWpsSettings } from '../../../../../api/Payroll/wps';
import { WpsSettingsView } from '../../../../../models/payroll';
import { getApiErrorMessage } from '../../../../../utils/apiErrors';
import { cn } from '../../../../../utils/cn';
import { WarningList } from '../../../../Payroll/WpsCenter/components/InlineConfirm';
import { useWpsConfirm } from '../../../../Payroll/WpsCenter/components/useWpsConfirm';

const SettingRow: React.FC<{ label: string; description: string; children: React.ReactNode }> = ({ label, description, children }) => (
  <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
    <div className="min-w-0">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="mt-0.5 max-w-[62ch] text-sm text-muted-foreground">{description}</p>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

interface ModeOption<T extends string> {
  value: T;
  label: string;
}

const ModeChooser = <T extends string>({ label, value, options, onChange, testId }: {
  label: string;
  value: T;
  options: ModeOption<T>[];
  onChange: (value: T) => void;
  testId: string;
}) => (
  <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map(option => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          data-value={option.value}
          className={cn(
            'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
            selected
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-background text-muted-foreground hover:border-primary/50',
          )}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const isValidThreshold = (value: number): boolean => Number.isInteger(value) && value >= 1 && value <= 100;

/**
 * The UAE WPS settings on Compliance Config. HR only: the page renders this for a UAE company and not for the Operator,
 * and the route is HrOnly. The route takes a partial update, and with nothing confirmed it changes nothing and answers
 * with named warnings, which show directly above the save bar until HR saves anyway or changes something.
 */
const WpsSettingsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [baseline, setBaseline] = useState<WpsSettingsView | null>(null);
  const [form, setForm] = useState<WpsSettingsView | null>(null);
  // The field is typed freely, so an empty or partial value is not forced back to a number while HR types.
  const [threshold, setThreshold] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const result = await getWpsSettings();
      if (result.errors.length > 0 || !result.payload) {
        setLoadError(getApiErrorMessage(result, t));
        return;
      }
      setBaseline(result.payload);
      setForm(result.payload);
      setThreshold(String(result.payload.wageShareThresholdPercent));
    } catch (e) {
      setLoadError(getApiErrorMessage(e, t));
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const thresholdValue = Number(threshold);
  const thresholdValid = threshold.trim() !== '' && isValidThreshold(thresholdValue);

  const current = useMemo<WpsSettingsView | null>(
    () => (form ? { ...form, wageShareThresholdPercent: thresholdValid ? thresholdValue : form.wageShareThresholdPercent } : null),
    [form, thresholdValid, thresholdValue],
  );

  const changes = useMemo<Partial<WpsSettingsView>>(() => {
    if (!current || !baseline) return {};
    const entries = (Object.keys(baseline) as (keyof WpsSettingsView)[])
      .filter(key => current[key] !== baseline[key])
      .map(key => [key, current[key]]);
    return Object.fromEntries(entries) as Partial<WpsSettingsView>;
  }, [current, baseline]);

  const dirty = Object.keys(changes).length > 0 || !thresholdValid;

  const finished = useCallback(() => {
    setBaseline(current);
    setSaved(true);
  }, [current]);
  const { warnings, error, busy, run, reset } = useWpsConfirm(finished);

  const patch = useCallback((change: Partial<WpsSettingsView>) => {
    setForm(prev => (prev ? { ...prev, ...change } : prev));
    setSaved(false);
    // A warning describes the values it was raised for, so changing one withdraws it.
    reset();
  }, [reset]);

  if (loadError) {
    return (
      <section className="rounded-xl border border-border bg-card p-5" data-testid="wps-settings">
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">{loadError}</p>
        <Button size="xs" variant="ghost" className="mt-1" onClick={() => void load()}>{t('Try again')}</Button>
      </section>
    );
  }

  if (!form) {
    return (
      <div className="flex justify-center rounded-xl border border-border bg-card py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const warnOrRefuse: ModeOption<'Warn' | 'Refuse'>[] = [
    { value: 'Warn', label: t('payroll.wps.uae.settings.modeWarn') },
    { value: 'Refuse', label: t('payroll.wps.uae.settings.modeRefuse') },
  ];

  return (
    <section aria-labelledby="wps-settings-heading" className="space-y-4" data-testid="wps-settings">
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <header className="border-b border-border bg-muted/40 px-5 py-4">
          <h3 id="wps-settings-heading" className="text-base font-semibold text-foreground">
            {t('payroll.wps.uae.settings.title')}
          </h3>
          <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">{t('payroll.wps.uae.settings.intro')}</p>
        </header>

        <div className="divide-y divide-border">
          <SettingRow label={t('payroll.wps.uae.settings.filedOutside')} description={t('payroll.wps.uae.settings.filedOutsideDesc')}>
            <Switch
              checked={form.filedOutsideMowazaf}
              onCheckedChange={value => patch({ filedOutsideMowazaf: value })}
              aria-label={t('payroll.wps.uae.settings.filedOutside')}
              data-testid="wps-setting-filed-outside"
            />
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.holdBack')} description={t('payroll.wps.uae.settings.holdBackDesc')}>
            <Switch
              checked={form.holdBackEnabled}
              onCheckedChange={value => patch({ holdBackEnabled: value })}
              aria-label={t('payroll.wps.uae.settings.holdBack')}
              data-testid="wps-setting-hold-back"
            />
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.paidOutside')} description={t('payroll.wps.uae.settings.paidOutsideDesc')}>
            <Switch
              checked={form.paidOutsideEnabled}
              onCheckedChange={value => patch({ paidOutsideEnabled: value })}
              aria-label={t('payroll.wps.uae.settings.paidOutside')}
              data-testid="wps-setting-paid-outside"
            />
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.threshold')} description={t('payroll.wps.uae.law.share')}>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={100}
                step={1}
                className="w-24"
                value={threshold}
                aria-label={t('payroll.wps.uae.settings.threshold')}
                aria-invalid={!thresholdValid}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setThreshold(e.target.value);
                  setSaved(false);
                  reset();
                }}
                data-testid="wps-setting-threshold"
              />
              <span className="text-sm text-muted-foreground" aria-hidden="true">%</span>
            </div>
            {!thresholdValid && (
              <p className="mt-1 text-sm text-red-700 dark:text-red-300" role="alert">{t('payroll.wps.uae.err.settingThresholdRange')}</p>
            )}
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.iban')} description={t('payroll.wps.uae.settings.ibanDesc')}>
            <ModeChooser
              label={t('payroll.wps.uae.settings.iban')}
              value={form.ibanChecksumMode}
              options={[
                { value: 'Off', label: t('payroll.wps.uae.settings.modeOff') },
                { value: 'Warn', label: t('payroll.wps.uae.settings.modeWarn') },
                { value: 'Block', label: t('payroll.wps.uae.settings.modeBlock') },
              ]}
              onChange={value => patch({ ibanChecksumMode: value })}
              testId="wps-setting-iban"
            />
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.markPaidOnHeld')} description={t('payroll.wps.uae.settings.markPaidOnHeldDesc')}>
            <ModeChooser
              label={t('payroll.wps.uae.settings.markPaidOnHeld')}
              value={form.markPaidOnHeldMode}
              options={warnOrRefuse}
              onChange={value => patch({ markPaidOnHeldMode: value })}
              testId="wps-setting-mark-paid-held"
            />
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.regenerate')} description={t('payroll.wps.uae.settings.regenerateDesc')}>
            <ModeChooser
              label={t('payroll.wps.uae.settings.regenerate')}
              value={form.regenerateInFileMode}
              options={warnOrRefuse}
              onChange={value => patch({ regenerateInFileMode: value })}
              testId="wps-setting-regenerate"
            />
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.noPayrollRow')} description={t('payroll.wps.uae.settings.noPayrollRowDesc')}>
            <ModeChooser
              label={t('payroll.wps.uae.settings.noPayrollRow')}
              value={form.noPayrollRowMode}
              options={[
                { value: 'KeepOpen', label: t('payroll.wps.uae.settings.modeKeepOpen') },
                { value: 'WarnOnly', label: t('payroll.wps.uae.settings.modeWarnOnly') },
              ]}
              onChange={value => patch({ noPayrollRowMode: value })}
              testId="wps-setting-no-payroll"
            />
          </SettingRow>
          <SettingRow label={t('payroll.wps.uae.settings.halfPaid')} description={t('payroll.wps.uae.settings.halfPaidDesc')}>
            <ModeChooser
              label={t('payroll.wps.uae.settings.halfPaid')}
              value={form.halfPaidLeaveMode}
              options={[
                { value: 'Zero', label: t('payroll.wps.uae.settings.modeZero') },
                { value: 'Half', label: t('payroll.wps.uae.settings.modeHalf') },
              ]}
              onChange={value => patch({ halfPaidLeaveMode: value })}
              testId="wps-setting-half-paid"
            />
          </SettingRow>
        </div>
      </div>

      {warnings && <WarningList warnings={warnings} />}
      {error && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{error}</p>}
      {saved && (
        <p className="text-sm text-muted-foreground" role="status" data-testid="wps-settings-saved">
          {t('payroll.wps.uae.settings.saved')}
        </p>
      )}

      <StickyActionBar
        hasUnsavedChanges={dirty}
        isSaving={busy}
        onReset={() => {
          if (baseline) {
            setForm(baseline);
            setThreshold(String(baseline.wageShareThresholdPercent));
          }
          setSaved(false);
          reset();
        }}
        onSave={() => void run(() => putWpsSettings({ ...changes, confirm: warnings !== null }))}
        t={t as (key: string) => string}
        saveLabel={warnings ? t('payroll.wps.uae.settings.saveAnyway') : t('payroll.wps.uae.settings.save')}
        saveDisabled={!thresholdValid}
        saveTestId="wps-settings-save"
      />
    </section>
  );
};

export default WpsSettingsPanel;
